import { TOKEN_ADDRESSES } from '@kwenta/sdk/constants'
import { PerpsProvider, SupportedNetworkIds } from '@kwenta/sdk/types'
import { useCallback, useMemo } from 'react'
import { withdrawFundsFromOneClick } from 'state/futures/actions'
import { selectAccountContext } from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectOneClickTradingWalletAddress } from 'state/wallet/selectors'
import { AbstractionToken } from 'types/accountAbstraction'
import { ONE_MINUTE_IN_MS } from 'utils/dates'
import { erc20Abi, formatUnits } from 'viem'
import { useBalance, useReadContract } from 'wagmi'

type WithdrawalConfig = {
	chainId: number
	decimals: number
	token: AbstractionToken
}

const WITHDRAWAL_CONFIGS: Partial<Record<PerpsProvider, WithdrawalConfig>> = {
	[PerpsProvider.SNX_V3_BASE]: {
		chainId: SupportedNetworkIds.BASE_MAINNET,
		decimals: 6,
		token: AbstractionToken.USDC,
	},
	[PerpsProvider.SNX_V2_OP]: {
		chainId: SupportedNetworkIds.OPTIMISM_MAINNET,
		decimals: 18,
		token: AbstractionToken.ETH,
	},
}

export function useWithdrawal() {
	const dispatch = useAppDispatch()
	const address = useAppSelector(selectOneClickTradingWalletAddress)
	const { provider } = useAppSelector(selectAccountContext)
	const config = WITHDRAWAL_CONFIGS[provider]

	const isEnabled = !!address && !!config

	const { data: usdcBalance, refetch: refetchUsdc } = useReadContract({
		chainId: config?.chainId,
		address:
			config?.token === AbstractionToken.USDC
				? TOKEN_ADDRESSES.USDC[SupportedNetworkIds.BASE_MAINNET]
				: undefined,
		abi: erc20Abi,
		args: [address!],
		functionName: 'balanceOf',
		query: {
			enabled: isEnabled && config?.token === AbstractionToken.USDC,
			refetchInterval: ONE_MINUTE_IN_MS * 3,
			refetchOnMount: true,
		},
	})

	const { data: ethBalance, refetch: refetchEth } = useBalance({
		chainId: config?.chainId,
		address: address!,
		query: {
			enabled: isEnabled && config?.token === AbstractionToken.ETH,
			refetchInterval: ONE_MINUTE_IN_MS * 3,
			refetchOnMount: true,
		},
	})

	const balance = useMemo(() => {
		if (!config) return
		if (config.token === AbstractionToken.USDC) return usdcBalance
		return ethBalance?.value
	}, [config, usdcBalance, ethBalance])

	const withdrawalText = useMemo(() => {
		if (!balance || !config) return ''
		return `Withdraw ${formatUnits(balance, config.decimals)} ${config.token}`
	}, [balance, config])

	const withdraw = useCallback(async () => {
		if (!balance || !config) return

		await dispatch(
			withdrawFundsFromOneClick({
				amount: formatUnits(balance, config.decimals),
				token: config.token,
			})
		)

		if (config.token === AbstractionToken.USDC) {
			await refetchUsdc()
		} else {
			await refetchEth()
		}
	}, [balance, config, dispatch, refetchUsdc, refetchEth])

	return {
		balance,
		withdraw,
		withdrawalText,
	}
}
